import { MouseEvent, useState } from 'react';
import { MenuItem, TableCell, TableRow } from '@mui/material';
import { Iconify } from '@/components';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import { servicesManagementPath } from '@/features/services-management';
import {
  formatBlockedDateDisplay,
  getBlockedDateCityName,
  getBlockedDateServiceName,
  getBlockedDateVariantName,
  getDayOfWeekLabel,
} from '../utils';
import { ServiceBlockedDate } from '../types';

type BlockedDatesTableRowProps = {
  row: ServiceBlockedDate;
  canUpdate: boolean;
  canDelete: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function BlockedDatesTableRow({
  row,
  canUpdate,
  canDelete,
  onEditRow,
  onDeleteRow,
}: BlockedDatesTableRowProps) {
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleEdit = () => {
    onEditRow();
    handleCloseMenu();
  };

  const handleDelete = () => {
    onDeleteRow();
    handleCloseMenu();
  };

  return (
    <TableRow hover>
      <TableCell align="left">
        {formatBlockedDateDisplay(row.start_date)}
      </TableCell>

      <TableCell align="left">
        {formatBlockedDateDisplay(row.end_date)}
      </TableCell>

      <TableCell align="left">
        {getDayOfWeekLabel(row.day_of_the_week)}
      </TableCell>

      <TableCell align="left">{getBlockedDateCityName(row)}</TableCell>

      <TableCell align="left">
        {row.service_variant_id && row.service_variant ? (
          <TableRowLink
            linkText={getBlockedDateVariantName(row)}
            linkTo={servicesManagementPath('editVariant', {
              variantId: String(row.service_variant_id),
              tabSid: 'details',
            })}
          />
        ) : (
          getBlockedDateVariantName(row)
        )}
      </TableCell>

      <TableCell align="left">
        {row.service_variant?.service?.id ? (
          <TableRowLink
            linkText={getBlockedDateServiceName(row)}
            linkTo={servicesManagementPath('editService', {
              serviceId: String(row.service_variant.service.id),
            })}
          />
        ) : (
          getBlockedDateServiceName(row)
        )}
      </TableCell>

      <TableCell align="right">
        {(canUpdate || canDelete) && (
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              <>
                {canUpdate && (
                  <MenuItem onClick={handleEdit}>
                    <Iconify icon="eva:edit-outline" />
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <Iconify icon="eva:trash-2-outline" />
                    Delete
                  </MenuItem>
                )}
              </>
            }
          />
        )}
      </TableCell>
    </TableRow>
  );
}
